import React, {useState, Fragment, useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import Backbone from 'backbone';
import page from '../../../helpers/page';
import analytics from '../../../helpers/analytics';
import utility, {isMobileViewport} from '../../../helpers/utility';
import message from 'framework/modules/message';
import constants from '../../../helpers/constants';
import CarouselContainer from './carousel-container';
import './image-carousel.less';
import abacusUtil from '../../../helpers/abacus-util';
import ReactDOM from 'react-dom';

let totalImagesClicked = [];

const Preview = ({children, images, headline}) => {
    const [fullScreen, setFullScreen] = useState(false);
    const buttonRef = useRef(null);
    const scrollPositionRef = useRef(0);
    const showInlineCarousel = abacusUtil.showInlineCarousel();
    const portalNode = useRef(document.createElement('div')).current; // create a new div element to render the fullscreen carousel in a portal

    // add the portal node to the body
    useEffect(() => {
        portalNode.setAttribute('tabindex', '-1'); // set the tabindex to -1 to make the portal node focusable
        portalNode.setAttribute('aria-hidden', 'true');
        document.body.appendChild(portalNode);
        return () => {
            document.body.removeChild(portalNode);
        };
    }, [portalNode]);

    // handle focus and accessibility when the fullscreen carousel is opened or closed
    useEffect(() => {
        if (!portalNode) return;

        const container = document.getElementById('container');
        if (fullScreen) {
            // Store previous focus
            const prevFocus = document.activeElement;

            // Hide all body children except our portal from screen readers
            const bodyChildren = Array.from(document.body.children);
            const elementsToHide = [];

            bodyChildren.forEach((el) => {
                if (el !== portalNode && !portalNode.contains(el)) {
                    el.setAttribute('aria-hidden', 'true');
                    // Use inert for browsers that support it (gracefully degrades)
                    if ('inert' in el) {
                        el.inert = true;
                    }
                    elementsToHide.push(el);
                }
            });

            // Set up dialog semantics on portal
            portalNode.setAttribute('aria-hidden', 'false');
            portalNode.setAttribute('role', 'dialog');
            portalNode.setAttribute('aria-modal', 'true');
            portalNode.setAttribute('aria-label', headline || 'Image gallery');

            portalNode.focus();
            if (container) {
                container.setAttribute('aria-hidden', 'true');
            }

            const prevOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';

            // Store cleanup data
            portalNode._a11yCleanup = {
                elementsToHide,
                prevFocus,
                prevOverflow,
            };
        } else {
            // Restore accessibility when carousel closes
            const cleanup = portalNode._a11yCleanup;

            setTimeout(() => {
                portalNode.setAttribute('aria-hidden', 'true');
                portalNode.removeAttribute('role');
                portalNode.removeAttribute('aria-modal');
                portalNode.removeAttribute('aria-label');

                if (cleanup) {
                    // Restore hidden elements
                    cleanup.elementsToHide.forEach((el) => {
                        el.removeAttribute('aria-hidden');
                        if ('inert' in el) {
                            el.inert = false;
                        }
                    });

                    // Restore scroll and focus
                    document.body.style.overflow = cleanup.prevOverflow || '';

                    // Clean up stored data
                    delete portalNode._a11yCleanup;
                }

                // Container handling for backwards compatibility
                if (container) {
                    container.removeAttribute('aria-hidden');
                }
            }, 0);
        }
    }, [fullScreen, portalNode, headline]);

    const countClickedImages = (index) => {
        if (totalImagesClicked.indexOf(index) === -1) {
            totalImagesClicked.push(index);
        }
    };

    const onImageClick = (event) => {
        if (
            showInlineCarousel &&
            event.target.className.includes('carousel-desktop__nav')
        ) {
            return;
        }
        scrollPositionRef.current = window.scrollY;
        setFullScreen(true);
        page.addWindowHash(constants.PAGE.CAROUSEL_MODAL_OPEN_HASH); // let the app know that it needs not move forward in history when closing this modal
    };

    const onClose = () => {
        let numImagesChecked =
            totalImagesClicked.length > 0 ? totalImagesClicked.length : 1;
        analytics.genericEventWithLabel(
            'close carousel clicked',
            numImagesChecked
        );
        totalImagesClicked = [];
        Backbone.trigger(constants.EVENTS.CLOSE_IMAGE_CAROUSEL); // this is used to reset the urgency modal to show
        // go back in history to ensure the link back button works correctly
        if (history.back) {
            history.back();
        }
        setFullScreen(false);
        // focus the view photos button when the carousel is closed, setTimeout to ensure CarouselContainer is unmounted
        setTimeout(() => {
            // iOS Safari ignores overflow:hidden on body, causing scroll-to-bottom on carousel close (VRBOCXE-3780)
            if (
                (utility.isSafariIOS() || utility.isNativeMobileIOS()) &&
                typeof scrollPositionRef.current === 'number'
            ) {
                window.scrollTo(0, scrollPositionRef.current);
            }
            if (buttonRef.current) {
                buttonRef.current.focus();
            }
        }, 100);
    };

    return (
        <Fragment>
            <div
                className="carousel-preview__image-row-container"
                onClick={onImageClick}
            >
                {!showInlineCarousel && (
                    <div className="carousel-preview__image-row">
                        {children}
                    </div>
                )}
                {showInlineCarousel && (
                    <CarouselContainer
                        images={images}
                        fullScreen={false}
                        onClose={onClose}
                        countClickedImages={countClickedImages}
                        isInline
                        headline={headline}
                    />
                )}
                <button
                    id="carousel-preview_button"
                    ref={buttonRef}
                    className={`btn btn-overlay btn-xs ${
                        showInlineCarousel ? 'inline-button' : ''
                    }`}
                    type="button"
                    onClick={onImageClick}
                    tabIndex={0}
                    data-testid="wdio_cko_carousel-preview-button"
                >
                    {message.read('tc.view.photos')}
                </button>
            </div>
            {ReactDOM.createPortal(
                <CarouselContainer
                    images={images}
                    fullScreen={fullScreen}
                    onClose={onClose}
                    countClickedImages={countClickedImages}
                    headline={headline}
                />,
                portalNode
            )}
        </Fragment>
    );
};

const CarouselPreviewContainer = ({images, thumbnailUrl, headline}) => {
    const listingPhotos =
        images && images.length
            ? Array.prototype.slice.apply(images, [0, 3])
            : null;
    const transformedThumbnailUrl =
        thumbnailUrl && thumbnailUrl.indexOf('.TREATMENT.') !== -1
            ? thumbnailUrl.replace('.TREATMENT.', '.c4.')
            : thumbnailUrl;
    const getImgStyle = (url) => {
        return {
            background: `url(${url}) no-repeat center center`,
            backgroundSize: 'cover',
        };
    };
    const showInlineCarousel = abacusUtil.showInlineCarousel();

    return (
        <div className="carousel-preview">
            {listingPhotos && isMobileViewport() && (
                <Preview images={images} headline={headline}>
                    <div
                        style={getImgStyle(transformedThumbnailUrl)}
                        className="property-image"
                    />
                </Preview>
            )}
            {!isMobileViewport() && (
                <Fragment>
                    {listingPhotos && (
                        <Preview images={images} headline={headline}>
                            {!showInlineCarousel &&
                                listingPhotos.map((photo, index) => {
                                    return (
                                        <div
                                            key={index}
                                            style={getImgStyle(photo.c5_uri)}
                                            className="property-image-thumbnail"
                                        />
                                    );
                                })}
                        </Preview>
                    )}
                    {!listingPhotos && transformedThumbnailUrl && (
                        <img
                            src={transformedThumbnailUrl}
                            className="property-image"
                            alt="property image"
                        />
                    )}
                </Fragment>
            )}
        </div>
    );
};

CarouselPreviewContainer.propTypes = {
    images: PropTypes.array,
    thumbnailUrl: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    headline: PropTypes.string,
};

CarouselPreviewContainer.defaultProps = {
    images: null,
    thumbnailUrl: false,
    headline: '',
};

export default CarouselPreviewContainer;
